import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import BadgeTier from "../components/BadgeTier";
import api, { BACKEND_URL } from "../lib/api";
import { ArrowRight, Sparkles, Clock, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function toAbs(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function CaseStudiesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/case-studies")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load case studies"))
      .finally(() => setLoading(false));
  }, []);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Case Studies · Real Brand-Creator Collabs on Nexus"
        description="Real completed collaborations on Nexus, from brief to delivery to results. Learn how brands and creators are actually working together and what the outcomes look like."
        path="/case-studies"
        keywords={[
          "creator marketing case studies",
          "influencer campaign case study",
          "UGC campaign results",
          "TikTok creator case study",
          "Instagram creator campaign",
          "brand creator collaboration examples",
        ]}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5" /> Case studies
          </div>
          <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter leading-[0.95]">
            Real collabs. <span className="nexus-gradient-text">Real results.</span>
          </h1>
          <p className="text-zinc-400 mt-4 text-lg">
            Every case study comes from a completed, escrow-released hire — no vanity metrics.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-[#0C0C10] hairline animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="glass rounded-3xl p-14 text-center" data-testid="case-studies-empty">
            <Sparkles className="h-8 w-8 mx-auto mb-3 text-nexus-purple" />
            <div className="font-display text-2xl mb-2">First case study incoming</div>
            <p className="text-zinc-400 max-w-lg mx-auto">
              As brands and creators complete deals on Nexus, each released hire can be published as a case study for the world to see.
            </p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="case-studies-list">
            {featured && (
              <Link
                to={`/case-studies/${featured.slug}`}
                className="grid lg:grid-cols-2 rounded-3xl overflow-hidden hairline bg-[#0C0C10] card-hover beam-hover"
                data-testid="case-study-featured"
              >
                <div className="aspect-[16/9] lg:aspect-auto overflow-hidden relative">
                  {featured.cover_image ? (
                    <img src={toAbs(featured.cover_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full nexus-gradient opacity-30" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C10] via-transparent to-transparent" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-zinc-500">
                    <span className="text-nexus-blue">{featured.brand_name}</span>
                    <span>·</span>
                    <span>{formatDate(featured.published_at)}</span>
                  </div>
                  <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight">
                    {featured.title}
                  </h2>
                  <p className="text-zinc-300 mt-4 max-w-xl">{featured.headline}</p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {(featured.creator_niches || []).slice(0, 3).map((n) => (
                      <span key={n} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.02]">{n}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-6">
                    {(featured.metrics || []).slice(0, 3).map((m, i) => (
                      <div key={i} className="p-3 rounded-xl bg-white/[0.02] hairline">
                        <div className="font-display font-black nexus-gradient-text text-lg leading-none">{m.value}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full overflow-hidden hairline bg-black/40">
                      {featured.creator_avatar && (
                        <img src={toAbs(featured.creator_avatar)} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="text-sm">
                      <div className="font-semibold">{featured.creator_name}</div>
                      <BadgeTier tier={featured.creator_badge_tier || "bronze"} size="sm" />
                    </div>
                    <span className="ml-auto nexus-gradient-text font-semibold flex items-center gap-1">
                      Read case study <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/case-studies/${c.slug}`}
                    className="rounded-2xl overflow-hidden hairline bg-[#0C0C10] card-hover beam-hover flex flex-col"
                    data-testid={`case-study-card-${c.slug}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      {c.cover_image ? (
                        <img src={toAbs(c.cover_image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full nexus-gradient opacity-30" />
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-widest text-nexus-blue mb-2">{c.brand_name}</div>
                      <h3 className="font-display font-bold text-lg leading-tight mb-2">{c.title}</h3>
                      <p className="text-sm text-zinc-400 line-clamp-3 flex-1">{c.headline}</p>
                      <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5">
                        {(c.metrics || []).slice(0, 3).map((m, i) => (
                          <div key={i} className="text-center">
                            <div className="font-display font-black nexus-gradient-text text-sm truncate">{m.value}</div>
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5 truncate">{m.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
